
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query";
import {
  createVaccination,
  getAllVaccine,
  getAllVacination,
  getVaccineCategories,
  getSopAggreement,
  getSopContraindiction,
  getVaccineBrand,
  getVaccineByVid,
  createAefi,
  getAefiCategories,
  getAefiTypes
} from "service";
import { useClients, useHfBranch, useStaff } from ".";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  IAllVaccinationResponse,
  ICreateVaccine,
  IVaccine,
  ISop,
  IVaccineBrand,
  IShotObject,
  ISopResponse,
  IAefiFormData,
  IShot,
  IFilteredData,
} from "types";
import { toast } from "sonner";
import debounce from "lodash.debounce";
import { useMemo } from "react";

export const useVaccinations = () => {
  const { branch, allBranchesOptions } = useHfBranch();
  const { allStaffOptions } = useStaff();
  const { clientArray, debounceSearch } = useClients();

  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [selectedUser, setSelectedUser] = useState<number | null>(null);
  const [formData, setFormData] = useState<ICreateVaccine[]>([]);
  const [pagination, setPagination] = useState<{ current: number; pageSize: number }>({
    current: 1,
    pageSize: 10,
  });
  const [filters, setFilters] = useState<IFilteredData>({
    client: "",
    client_ino: "",
    created_by: "",
    gender: "",
    is_complete: undefined,
    is_cowvaVerified: undefined,
    location: null,
    vaccine: "",
    vid: "",
  });
  const [sortOptions, setSortOptions] = useState<string[]>([]);
  const [aefiFormData, setAefiFormData] = useState<IAefiFormData>({
    client: 0,
    vaccination: 0,
    shot: 0,
    batch_number: "",
    category: "",
    type: 0,
    other_type: "",
    onset_date: "",
  });

  const {
    data: allVaccinationsData,
    isPending: totalVaccinationsIsLoading,
    error,
    isPlaceholderData,
    refetch
  } = useQuery<IAllVaccinationResponse>({
    queryKey: ["all-vaccinations", branch?.hfId, pagination.current, pagination.pageSize, filters, sortOptions],
    queryFn: async () => {
      if (!branch) return { results: [], count: 0, next: null, previous: null };

      // Apply filters and sorting to the API call
      const response = await getAllVacination(
        branch.hfId,
        pagination.current,
        pagination.pageSize,
        {
          client: filters.client || undefined,
          client_ino: filters.client_ino || undefined,
          created_by: filters.created_by || undefined,
          gender: filters.gender || undefined,
          is_complete: filters.is_complete,
          is_cowvaVerified: filters.is_cowvaVerified,
          location: filters.location || undefined,
          sort: sortOptions.length > 0 ? sortOptions : undefined,
          vaccine: filters.vaccine || undefined,
          vid: filters.vid || undefined,
        }
      );
      return response;
    },
    enabled: !!branch,
    staleTime: 0,
  });

  const handlePageChange = (page: number, pageSize?: number) => {
    const newPage = Math.max(1, page);
    setPagination(prev => ({
      current: newPage,
      pageSize: pageSize ?? prev.pageSize,
    }));
    // Force refetch after state update
    setTimeout(() => refetch(), 0);
  };

  // Update filters function
  const updateFilters = (newFilters: any) => {
    setFilters(newFilters);
    // Reset to first page when filters change
    setPagination(prev => ({
      ...prev,
      current: 1
    }));
    // Force refetch with the new filters
    setTimeout(() => refetch(), 0);
  };

  // Update sort function
  const updateSort = (newSortOptions: any) => {
    setSortOptions(newSortOptions);
    // Force refetch with the new sorting
    setTimeout(() => refetch(), 0);
  };



  const [vaccineSearchTerm, setVaccineSearchTerm] = useState("");


  const {
    data: clientVaccinations,
    isLoading: isLoadingClientVaccinations,
    error: clientVaccinationsError,
  } = useQuery<IAllVaccinationResponse>({
    queryKey: ["client-vaccinations", branch?.hfId, aefiFormData.client],
    queryFn: async () => {
      if (!branch || !aefiFormData.client) return { results: [], count: 0, next: null, previous: null };
      const response = await getAllVacination(
        branch.hfId,
        1,
        100,
        { client: String(aefiFormData.client) },
      );
      return response;
    },
    enabled: !!branch && !!aefiFormData.client,
  });

  const debounceVaccineSearch = useMemo(
    () =>
      debounce((search: string) => {
        setVaccineSearchTerm(search);
      }, 500, { leading: false, trailing: true }),
    []
  );

  useEffect(() => {
    return () => {
      debounceVaccineSearch.cancel();
    };
  }, [debounceVaccineSearch]);

  // Find vid from clientVaccinations.results
  const selectedVaccination = clientVaccinations?.results?.find(
    (v) => v.id === aefiFormData.vaccination
  );
  const vid = selectedVaccination?.vid;

  // Vaccination details query (using vid)
  const {
    data: vaccinationDetails,
    isLoading: isLoadingVaccinationDetails,
  } = useQuery<{ shots: IShot[] }>({
    queryKey: ["vaccination-details", branch?.hfId, vid],
    queryFn: async () => {
      if (!branch || !vid) {
        return { shots: [] };
      }
      const response = await getVaccineByVid(vid, branch.hfId);
      return response;
    },
    enabled: !!branch && !!vid,
  });

  // Auto-set shot when vaccinationDetails changes
  useEffect(() => {
    if (
      vaccinationDetails &&
      vaccinationDetails.shots &&
      vaccinationDetails.shots.length > 0 &&
      aefiFormData.vaccination
    ) {
      const defaultShot = vaccinationDetails.shots[0]; // Select first shot
      console.log("Auto-setting shot:", defaultShot);
      setAefiFormData((prev) => ({
        ...prev,
        shot: defaultShot.id,
        batch_number: "",
      }));
    } else {
      console.log("No shots available or vaccination not set:", {
        vaccinationDetails,
        vid,
        vaccination: aefiFormData.vaccination,
      });
      setAefiFormData((prev) => ({
        ...prev,
        shot: 0,
        batch_number: prev.batch_number,
      }));
    }
  }, [vaccinationDetails, aefiFormData.vaccination]);

  // AEFI categories query
  const {
    data: aefiCategories,
    isLoading: isLoadingAefiCategories,
  } = useQuery<{ label: string; value: string }[]>({
    queryKey: ["aefi-categories", branch?.hfId],
    queryFn: async () => {
      if (!branch) {
        return [];
      }
      const response = await getAefiCategories(branch.hfId);
      const categories = Object.entries(response).map(([key, value]) => ({
        label: value as string,
        value: key,
      }));
      return categories;
    },
    enabled: !!branch,
  });

  const {
    data: aefiTypes,
    isLoading: isLoadingAefiTypes,
  } = useQuery<{ label: string; value: number }[]>({
    queryKey: ["aefi-types", branch?.hfId, aefiFormData.category],
    queryFn: async () => {
      if (!branch || !aefiFormData.category) {
        return [];
      }
      const response = await getAefiTypes(branch.hfId, aefiFormData.category);
      return response.map((type: { id: number; name: string }) => ({
        label: type.name,
        value: type.id,
      }));
    },
    enabled: !!branch && !!aefiFormData.category,
  });
  // AEFI submission mutation
  const {
    mutate: createAefiMutation,
    isPending: createAefiIsPending,
  } = useMutation({
    mutationFn: async () => {
      if (!branch) throw new Error("Branch not found");
      console.log("Submitting AEFI:", aefiFormData);
      const response = await createAefi(branch.hfId, aefiFormData);
      return response;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["all-vaccinations", branch?.hfId] });
      toast.success("AEFI reported successfully");
      // navigate(`/${branch?.hfSlug}/vaccinations/aefi-history`);
    },
    onError: (err: any) => {
      const errors = err.response?.data.errors;
      if (Array.isArray(errors)) {
        toast.error(errors.map((err) => err.detail).join(", "));
      } else {
        toast.error("An unknown error occurred.");
      }
    },
  });

  // Update AEFI form data
  const updateAefiFormData = (field: keyof IAefiFormData, value: any) => {
    setAefiFormData((prev) => {
      const newData = { ...prev, [field]: value };
      if (field === "category") {
        newData.type = 0;
        newData.other_type = "";
      }
      console.log("Updated aefiFormData:", newData);
      if (field === "client") {
        setVaccineSearchTerm("");
      }
      return newData;
    });
  };



  const { data: vaccinesData, isLoading: isLoadingVaccines } =
    useQuery<IVaccine>({
      queryKey: ["vaccines", formData.at(-1)?.vaccine_category],
      queryFn: async () => {
        const lastUpdatedCategory = formData.at(-1)?.vaccine_category;
        if (!lastUpdatedCategory) return [];
        return await getAllVaccine(lastUpdatedCategory);
      },
      enabled: formData.some((item) => item.vaccine_category),
    });

  const { data: vaccineBrand, isLoading: isLoadingVaccineBrand } =
    useQuery<IVaccineBrand>({
      queryKey: ["vaccine-brand", branch?.hfId, formData.at(-1)?.vaccine],
      queryFn: async () => {
        const lastUpdatedVaccine = formData.at(-1)?.vaccine;
        if (!lastUpdatedVaccine) return [];
        return await getVaccineBrand(branch!.hfId, lastUpdatedVaccine);
      },
      enabled: formData.some((item) => item.vaccine !== null),
    });

  const { data: sopAggreementData, isLoading: isLoadingSopAggreement } =
    useQuery<ISop>({
      queryKey: ["sop-aggreement", branch?.hfId, formData.at(-1)?.vaccine],
      queryFn: async () => {
        const lastUpdatedVaccine = formData.at(-1)?.vaccine;
        if (!lastUpdatedVaccine) return [];
        return await getSopAggreement(branch!.hfId, lastUpdatedVaccine);
      },
      enabled: formData.some((item) => item.vaccine !== null),
    });

  const { data: sopContradictionData, isLoading: isLoadingSopContraindiction } =
    useQuery<ISop>({
      queryKey: ["sop-contradiction", branch?.hfId, formData.at(-1)?.vaccine],
      queryFn: async () => {
        const lastUpdatedVaccine = formData.at(-1)?.vaccine;
        if (!lastUpdatedVaccine) return [];
        return await getSopContraindiction(branch!.hfId, lastUpdatedVaccine);
      },
      enabled: formData.some((item) => item.vaccine !== null),
    });

  const { data: vaccineCategories, isPending: isPendingCategories } =
    useSuspenseQuery({
      queryKey: ["vaccine-categories"],
      queryFn: async () => {
        const response = await getVaccineCategories();
        return response;
      },
    });

  const {
    mutate: createVaccinationMutation,
    isPending: createVaccinationIsPending,
  } = useMutation({
    mutationFn: async () => {
      if (!branch) return;
      console.log(formData);
      const response = await createVaccination(branch?.hfId, formData);
      return response;
    },
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({
        queryKey: ["all-vaccinations", branch?.hfId],
      });
      toast.success("Vaccination created successfully");
      navigate(`/${branch?.hfSlug}/vaccinations/all-vaccinations`);
    },
    onError: (err: any) => {
      const errors = err.response?.data.errors;
      if (Array.isArray(errors)) {
        const errorMessages = errors.map((err) => err.detail).join(", ");
        toast.error(errorMessages);
      } else {
        toast.error("An unknown error occurred.");
      }
    },
  });



  const addCategory = () => {
    setFormData((prev) => [
      ...prev,
      {
        id: Date.now().toString(),
        user: selectedUser!,
        vaccine: null,
        vaccine_category: "",
        shots: [],
      },
    ]);
  };

  const removeCategory = (id: string) => {
    setFormData((prev) => prev.filter((item) => item.vaccine_category !== id));
  };

  const addShot = (categoryId: string) => {
    setFormData((prev) =>
      prev.map((item) =>
        item.vaccine_category === categoryId
          ? {
            ...item,
            shots: [
              ...item.shots,
              {
                brand: 0,
                vaccine_batch_number: "",
                route: "",
                vaccinated_on: "",
                next_vaccination_day: undefined, // Initialize as undefined
                next_vaccination_day_interval: "",
                vaccine_expiry_date: "",
                vaccinated_by: 0,
                branch: 0,
                sop_agreement_contraindications: [],
                sop_agreement_before_administration: [],
              },
            ],
          }
          : item
      )
    );
  };

  const removeShot = (categoryId: string, shotIndex: number) => {
    setFormData((prev) =>
      prev.map((item) =>
        item.vaccine_category === categoryId
          ? { ...item, shots: item.shots.filter((_, idx) => idx !== shotIndex) }
          : item
      )
    );
  };

  const updateCategory = (
    index: number,
    field: keyof ICreateVaccine,
    value: any
  ) => {
    setFormData((prev) =>
      prev.map((item, i) => (i === index ? { ...item, [field]: value } : item))
    );
  };

  const updateShot = (
    category: string,
    shotIndex: number,
    field: keyof IShotObject,
    value: any
  ) => {
    setFormData((prev) =>
      prev.map((item) =>
        item.vaccine_category === category
          ? {
            ...item,
            shots: item.shots.map((shot, idx) =>
              idx === shotIndex
                ? {
                  ...shot,
                  [field]: Array.isArray(value) ? [...value] : value,
                }
                : shot
            ),
          }
          : item
      )
    );
  };

  const immunisationArray = Object.entries(vaccineCategories).map(
    ([key, value]) => ({
      label: value,
      value: key,
    })
  );

  const vaccineArray = vaccinesData?.results
    ? vaccinesData.results.map((vaccine) => ({
      label: vaccine.name,
      value: vaccine.id,
    }))
    : [];

  const vaccineBrandArray =
    vaccineBrand?.results?.map((brand) => ({
      label: brand.name,
      value: brand.id,
    })) || [];

  const isFormComplete = () => {
    return (
      formData.length > 0 &&
      formData.every((item) => {
        return (
          item.vaccine_category &&
          item.vaccine &&
          item.shots.length > 0 &&
          item.shots.every((shot) => {
            return (
              shot.brand &&
              shot.vaccine_batch_number &&
              shot.vaccinated_on &&
              typeof shot.next_vaccination_day === "number" && // Check for number
              shot.next_vaccination_day > 0 && // Ensure positive number
              shot.next_vaccination_day_interval &&
              shot.vaccine_expiry_date &&
              shot.vaccinated_by &&
              shot.branch &&
              shot.sop_agreement_contraindications.length > 0 &&
              shot.sop_agreement_before_administration.length > 0
            );
          })
        );
      })
    );
  };


  const handleCreateVaccination = () => {
    createVaccinationMutation();
  };
  const {
    data: allVaccinations,
    fetchNextPage,
    hasNextPage,
    hasPreviousPage,
    isFetchingNextPage,
    isFetchingPreviousPage,
    fetchPreviousPage,
    isPending: allVaccinationsIsLoading,
  } = useInfiniteQuery<IAllVaccinationResponse>({
    queryKey: ["vac", branch],



    initialPageParam: 1,
    getNextPageParam: (lastPage) => {

      return null
    },
    getPreviousPageParam: (_, allPages) => {


      try {

        return null
      } catch (error) {

        return undefined;
      }
    },
    enabled: !!branch,
  });

  const loading =
    isPendingCategories ||
    createVaccinationIsPending || totalVaccinationsIsLoading

  return {
    vaccinesData,
    vaccineArray,

    isLoading: loading,
    isLoadingVaccines,
    isLoadingSopAggreement,
    isLoadingSopContraindiction,
    immunisationArray,
    formData,
    clientArray,
    allBranchesOptions,
    allStaffOptions,
    sopAggreementData,
    sopContradictionData,
    vaccineBrand,
    isLoadingVaccineBrand,
    vaccineBrandArray,

    setFormData,
    handleCreateVaccination,
    isFormComplete,

    setSelectedUser,
    selectedUser,
    addCategory,
    removeCategory,
    addShot,
    removeShot,
    updateCategory,
    updateShot,
    debounceSearch,
    handlePageChange,
    pagination,
    totalVaccinationsIsLoading,
    allVaccinationsData,
    isPlaceholderData,
    updateAefiFormData,
    aefiFormData,
    clientVaccinations,
    isLoadingClientVaccinations,
    debounceVaccineSearch,
    vaccineSearchTerm,
    createAefiIsPending,
    branch,
    createAefiMutation,
    vaccinationDetails,
    isLoadingVaccinationDetails,
    aefiCategories,
    isLoadingAefiCategories,
    aefiTypes,
    isLoadingAefiTypes,
    updateFilters,
    filters,
    setFilters,
    sortOptions,
    setSortOptions,
    updateSort,
    refetch,
    fetchNextPage,
    fetchPreviousPage,
    hasNextPage,
    isFetchingNextPage,
    hasPreviousPage,
    isFetchingPreviousPage,
    allVaccinations
  };
};